import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  CloudArrowUpIcon,
  DocumentIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  PencilSquareIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { Task } from '../TaskSidePanel';
import { supabase } from '../../../lib/supabase-auth';

interface Document {
  id: string;
  task_id?: string;
  name: string;
  size: number;
  uploadedAt: string;
  uploadedBy: string;
  status: 'unsigned' | 'signed' | 'pending_signature' | 'expired' | 'pending' | 'rejected';
  url?: string;
  signedAt?: string;
  expiresAt?: string;
  signature_required?: boolean;
}

interface DocumentsTabProps {
  task: Task;
}

export default function DocumentsTab({ task }: DocumentsTabProps) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  // Fetch documents from Supabase on component mount
  React.useEffect(() => {
    fetchDocuments();
  }, [task.id]);

  const fetchDocuments = async () => {
    try {
      // Try to fetch from compliance_documents table
      const { data, error } = await supabase
        .from('compliance_documents')
        .select('*')
        .eq('task_id', task.id)
        .order('uploaded_at', { ascending: false });

      if (error) {
        console.error('Error fetching documents:', error);
        // Use mock data if fetch fails
        setDocuments(mockDocuments);
      } else if (data) {
        // Transform data to match our interface
        const transformedDocs = data.map(doc => ({
          id: doc.id,
          task_id: doc.task_id,
          name: doc.name,
          size: doc.size,
          uploadedAt: doc.uploaded_at,
          uploadedBy: doc.uploaded_by,
          status: doc.status || 'unsigned',
          url: doc.url,
          signedAt: doc.signed_at,
          expiresAt: doc.expiry_date,
          signature_required: doc.signature_required,
        }));
        setDocuments(transformedDocs);
      }
    } catch (error) {
      console.error('Error in fetchDocuments:', error);
      // Use mock data as fallback
      setDocuments(mockDocuments);
    } finally {
      setLoading(false);
    }
  };

  // Mock data for demonstration
  const mockDocuments: Document[] = [
    {
      id: '1',
      name: 'Investment Policy Statement.pdf',
      size: 245000,
      uploadedAt: '2024-01-15T10:30:00Z',
      uploadedBy: 'John Smith',
      status: 'signed',
      signedAt: '2024-01-16T14:30:00Z',
    },
    {
      id: '2',
      name: 'Risk Assessment Form.pdf',
      size: 180000,
      uploadedAt: '2024-01-15T11:00:00Z',
      uploadedBy: 'Sarah Johnson',
      status: 'pending_signature',
    },
    {
      id: '3',
      name: 'Account Agreement.pdf',
      size: 320000,
      uploadedAt: '2024-01-14T09:00:00Z',
      uploadedBy: 'John Smith',
      status: 'unsigned',
    },
  ];

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setUploading(true);
    
    try {
      for (const file of acceptedFiles) {
        // Create a temporary document entry
        const tempDoc: Document = {
          id: Date.now().toString(),
          name: file.name,
          size: file.size,
          uploadedAt: new Date().toISOString(),
          uploadedBy: 'Current User', // Would come from auth context
          status: 'unsigned',
          signature_required: file.name.toLowerCase().includes('agreement') || 
                            file.name.toLowerCase().includes('contract'),
        };
        
        // Add to UI immediately for better UX
        setDocuments((prev) => [...prev, tempDoc]);
        
        // In production, you would upload to Supabase Storage here
        // const { data: uploadData, error: uploadError } = await supabase.storage
        //   .from('documents')
        //   .upload(`${task.id}/${Date.now()}-${file.name}`, file);
        
        // Then create a record in the compliance_documents table
        // const { error: dbError } = await supabase
        //   .from('compliance_documents')
        //   .insert({
        //     task_id: task.id,
        //     name: file.name,
        //     type: file.type,
        //     size: file.size,
        //     status: 'pending',
        //     uploaded_by: 'current_user_id',
        //     signature_required: tempDoc.signature_required,
        //     url: uploadData?.path,
        //   });
      }
    } catch (error) {
      console.error('Error uploading files:', error);
    } finally {
      setUploading(false);
    }
  }, [task.id]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'image/*': ['.png', '.jpg', '.jpeg'],
    },
  });

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusIcon = (status: Document['status']) => {
    switch (status) {
      case 'signed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'pending_signature':
        return <ClockIcon className="h-5 w-5 text-amber-500" />;
      case 'unsigned':
        return <XCircleIcon className="h-5 w-5 text-gray-400" />;
      case 'expired':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
    }
  };

  const getStatusText = (status: Document['status']) => {
    switch (status) {
      case 'signed':
        return 'Signed';
      case 'pending_signature':
        return 'Pending Signature';
      case 'unsigned':
        return 'Unsigned';
      case 'expired':
        return 'Expired';
    }
  };

  return (
    <div className="p-6">
      {/* Upload Area */}
      <div
        {...getRootProps()}
        className={`mb-6 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          isDragActive
            ? 'border-amber-500 bg-amber-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <input {...getInputProps()} />
        <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
        <p className="mt-2 text-sm text-gray-600">
          {isDragActive
            ? 'Drop the files here...'
            : 'Drag & drop files here, or click to select'}
        </p>
        <p className="text-xs text-gray-500 mt-1">
          PDF, DOC, DOCX, PNG, JPG up to 10MB
        </p>
      </div>

      {/* Upload Progress */}
      {uploading && (
        <div className="mb-4 p-4 bg-blue-50 rounded-lg">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
            <span className="text-sm text-blue-700">Uploading files...</span>
          </div>
        </div>
      )}

      {/* Documents List */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-gray-900">Task Documents</h3>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-500"></div>
          </div>
        ) : documents.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">
            No documents uploaded yet
          </p>
        ) : (
          <ul className="space-y-3">
            {documents.map((doc) => (
              <li
                key={doc.id}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <DocumentIcon className="h-10 w-10 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {doc.name}
                      </p>
                      <p className="text-sm text-gray-500">
                        {formatFileSize(doc.size)} • Uploaded by {doc.uploadedBy}
                      </p>
                      <p className="text-xs text-gray-400">
                        {formatDate(doc.uploadedAt)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-1">
                      {getStatusIcon(doc.status)}
                      <span
                        className={`text-xs font-medium ${
                          doc.status === 'signed'
                            ? 'text-green-700'
                            : doc.status === 'pending_signature'
                            ? 'text-amber-700'
                            : doc.status === 'expired'
                            ? 'text-red-700'
                            : 'text-gray-700'
                        }`}
                      >
                        {getStatusText(doc.status)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex items-center space-x-3">
                  <button className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500">
                    <EyeIcon className="h-4 w-4 mr-1" />
                    View
                  </button>
                  <button className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500">
                    <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                    Download
                  </button>
                  {(doc.status === 'unsigned' || doc.status === 'pending_signature') && (
                    <button className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500">
                      <PencilSquareIcon className="h-4 w-4 mr-1" />
                      Sign Document
                    </button>
                  )}
                </div>
                {doc.signedAt && (
                  <p className="mt-2 text-xs text-gray-500">
                    Signed on {formatDate(doc.signedAt)}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}